import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Briefcase, Mail, Phone, GraduationCap, Link2, FileText,
  Sparkles, UserPlus, Save, X, Clock, Calendar, Filter, Plus, Edit, Trash2, Eye, EyeOff,
} from 'lucide-react';

const STAGE_META = {
  applied:    { label: 'Applied',    color: 'bg-slate-100 text-slate-700' },
  screening:  { label: 'Screening',  color: 'bg-blue-100 text-blue-700' },
  interview:  { label: 'Interview',  color: 'bg-amber-100 text-amber-700' },
  offer:      { label: 'Offer',      color: 'bg-purple-100 text-purple-700' },
  hired:      { label: 'Hired',      color: 'bg-emerald-100 text-emerald-700' },
  rejected:   { label: 'Rejected',   color: 'bg-red-100 text-red-700' },
};
const STAGES = ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected'];

const AdminCareersDashboard = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('pipeline');
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [detailApp, setDetailApp] = useState(null);
  const [hireDialog, setHireDialog] = useState(null); // application object
  const [hireForm, setHireForm] = useState({ create_account: true, temporary_password: '', start_date: '' });
  const [stageDialog, setStageDialog] = useState(null); // { app, targetStage }
  const [stageNote, setStageNote] = useState('');
  const [stageFilter, setStageFilter] = useState(null);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [sendingOfferId, setSendingOfferId] = useState(null);
  // Pipeline extras — interviews, messages, resources
  const [pipelineTab, setPipelineTab] = useState('overview');
  const [interviewDraft, setInterviewDraft] = useState({ scheduled_at: '', duration_min: 30, mode: 'video', meeting_link: '', notes: '' });
  const [messageDraft, setMessageDraft] = useState({ message: '', send_email: true });
  const [resourceDraft, setResourceDraft] = useState({ kind: 'url', title: '', url: '', note: '' });
  const [pipelineBusy, setPipelineBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, appsRes] = await Promise.all([
        api.get('/admin/careers/jobs'),
        api.get('/admin/careers/applications'),
      ]);
      setJobs(jobsRes.data?.jobs || []);
      setApps(appsRes.data?.applications || []);
      setStageCounts(appsRes.data?.stage_counts || {});
    } catch {
      toast.error('Failed to load careers data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openStageChange = (app, targetStage) => {
    if (targetStage === 'hired') {
      setHireForm({ create_account: true, temporary_password: '', start_date: '' });
      setHireDialog(app);
      return;
    }
    setStageNote('');
    setStageDialog({ app, targetStage });
  };

  const applyStageChange = async () => {
    try {
      await api.patch(`/admin/careers/applications/${stageDialog.app.id}`, {
        stage: stageDialog.targetStage,
        note: stageNote || null,
      });
      toast.success(`Moved to ${STAGE_META[stageDialog.targetStage].label}`);
      setStageDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stage');
    }
  };

  const submitHire = async () => {
    try {
      const res = await api.post(`/admin/careers/applications/${hireDialog.id}/hire`, hireForm);
      toast.success(`Hired ${hireDialog.full_name}!`);
      if (res.data?.temporary_password) {
        toast.info(`Temporary password: ${res.data.temporary_password}`, { duration: 12000 });
      }
      setHireDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hire');
    }
  };

  const closeJob = async (job) => {
    if (!window.confirm(`Unpublish "${job.title}"? Applicants will no longer see it.`)) return;
    try {
      await api.patch(`/admin/careers/jobs/${job.id}`, { status: 'closed' });
      toast.success('Job unpublished');
      load();
    } catch { toast.error('Failed to close job'); }
  };

  const reopenJob = async (job) => {
    try {
      await api.patch(`/admin/careers/jobs/${job.id}`, { status: 'open' });
      toast.success('Job published');
      load();
    } catch { toast.error('Failed to reopen job'); }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/careers/jobs/${job.id}`);
      toast.success('Job deleted');
      load();
    } catch { toast.error('Failed to delete job'); }
  };

  const [jobEditor, setJobEditor] = useState(null);
  const [savingJob, setSavingJob] = useState(false);

  const emptyJob = () => ({
    id: '', slug: '', title: '', department: 'Engineering',
    role_key: 'social_media', status: 'open', location: 'Remote',
    hours_per_week: 10, duration_weeks: 12,
    summary: '', description: '', responsibilities: [], perks: [],
    apply_url: '', posted_at: new Date().toISOString(),
  });

  const openCreateJob = () => setJobEditor({ mode: 'create', job: emptyJob() });
  const openEditJob = (j) => setJobEditor({ mode: 'edit', job: { ...j, responsibilities: j.responsibilities || [], perks: j.perks || [] } });

  const saveJobEditor = async () => {
    if (!jobEditor) return;
    const j = jobEditor.job;
    if (!j.title.trim() || !j.slug.trim() || !j.role_key || !j.summary.trim()) {
      toast.error('title, slug, role_key and summary are required');
      return;
    }
    setSavingJob(true);
    try {
      if (jobEditor.mode === 'create') {
        await api.post('/admin/careers/jobs', j);
        toast.success('Job created & published');
      } else {
        const { id, application_count, ...updates } = j;
        await api.patch(`/admin/careers/jobs/${id}`, updates);
        toast.success('Job saved');
      }
      setJobEditor(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSavingJob(false); }
  };

  const generateOfferLetter = async (app) => {
    if (!app?.id) return;
    setGeneratingLetter(true);
    try {
      const res = await api.get(`/admin/careers/applications/${app.id}/offer-letter`);
      const { html, filename } = res.data || {};
      if (!html) throw new Error('No letter returned');
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w || w.closed || typeof w.closed === 'undefined') {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'zazu-offer-letter.html';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        toast.success('Offer letter downloaded — popup was blocked.');
      } else { toast.success('Offer letter opened in a new tab.'); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate offer letter');
    } finally { setGeneratingLetter(false); }
  };

  const sendOfferEmail = async (app) => {
    if (!app?.id) return;
    const startDate = window.prompt(
      `Email the offer to ${app.full_name} (${app.email})?\n\nEnter the proposed start date (leave blank for "To be mutually agreed"):`,
      app.offer_start_date || ''
    );
    // Cancel returns null; empty string is a valid "TBD" choice
    if (startDate === null) return;
    setSendingOfferId(app.id);
    try {
      const res = await api.post(`/admin/careers/applications/${app.id}/send-offer`, {
        start_date: startDate || null,
      });
      if (res.data?.email_sent) {
        toast.success(`Offer emailed to ${res.data.applicant_email}. Awaiting response.`);
      } else {
        toast.success('Offer recorded — email sending is disabled in this environment. Share the accept link manually.');
      }
      // Refresh detail view so the "offer sent" chip appears
      if (detailApp?.id === app.id) await refreshDetailApp(app.id);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to email offer');
    } finally {
      setSendingOfferId(null);
    }
  };

  const refreshDetailApp = async (appId) => {
    try {
      const res = await api.get(`/admin/careers/applications/${appId}`);
      setDetailApp(res.data);
    } catch { /* silent */ }
  };

  const addInterview = async () => {
    if (!detailApp?.id || !interviewDraft.scheduled_at) { toast.error('Pick a date/time first'); return; }
    setPipelineBusy(true);
    try {
      await api.post(`/admin/careers/applications/${detailApp.id}/interviews`, interviewDraft);
      toast.success('Interview scheduled');
      setInterviewDraft({ scheduled_at: '', duration_min: 30, mode: 'video', meeting_link: '', notes: '' });
      await refreshDetailApp(detailApp.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to schedule'); }
    finally { setPipelineBusy(false); }
  };
  const updateInterviewStatus = async (iid, status) => {
    try {
      await api.patch(`/admin/careers/applications/${detailApp.id}/interviews/${iid}`, { status });
      toast.success('Interview updated');
      await refreshDetailApp(detailApp.id);
    } catch { toast.error('Failed to update interview'); }
  };
  const addMessage = async () => {
    if (!detailApp?.id || !messageDraft.message.trim()) return;
    setPipelineBusy(true);
    try {
      const res = await api.post(`/admin/careers/applications/${detailApp.id}/messages`, messageDraft);
      toast.success(res.data?.message?.emailed ? 'Message sent + emailed' : 'Message saved');
      setMessageDraft({ message: '', send_email: true });
      await refreshDetailApp(detailApp.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send'); }
    finally { setPipelineBusy(false); }
  };
  const addResource = async () => {
    if (!detailApp?.id || !resourceDraft.title.trim()) return;
    setPipelineBusy(true);
    try {
      await api.post(`/admin/careers/applications/${detailApp.id}/resources`, resourceDraft);
      toast.success('Resource added');
      setResourceDraft({ kind: 'url', title: '', url: '', note: '' });
      await refreshDetailApp(detailApp.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add resource'); }
    finally { setPipelineBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex-1">
            <h1 className="font-semibold text-slate-800 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Careers & Hiring</h1>
            <p className="text-xs text-slate-500">{apps.length} applications across {jobs.length} jobs</p>
          </div>
          <Link to="/careers" target="_blank">
            <Button variant="outline" size="sm">View public page →</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pipeline" data-testid="careers-tab-pipeline">Pipeline ({apps.length})</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="careers-tab-jobs">Jobs ({jobs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pipeline">
            {/* Stage summary — clickable to filter the kanban */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 my-4">
              {STAGES.map((s) => {
                const active = stageFilter === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStageFilter(active ? null : s)}
                    className={`text-left rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
                      active
                        ? 'ring-2 ring-emerald-500 shadow-md scale-[1.02]'
                        : 'hover:shadow-md hover:scale-[1.01]'
                    }`}
                    data-testid={`stage-tile-${s}`}
                    aria-pressed={active}
                  >
                    <Card className="border-0 shadow-sm h-full">
                      <CardContent className="p-3 text-center">
                        <Badge className={STAGE_META[s].color + ' mb-1'}>{STAGE_META[s].label}</Badge>
                        <p className="text-2xl font-bold text-slate-800">{stageCounts[s] || 0}</p>
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
            </div>

            {/* Filter status bar */}
            {stageFilter && (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3" data-testid="stage-filter-banner">
                <div className="flex items-center gap-2 text-sm text-emerald-800">
                  <Filter className="w-4 h-4" />
                  Showing <strong>{STAGE_META[stageFilter].label}</strong> only · {stageCounts[stageFilter] || 0} applications
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStageFilter(null)} data-testid="stage-filter-clear">
                  <X className="w-4 h-4 mr-1" /> Clear filter
                </Button>
              </div>
            )}

            {/* Kanban */}
            <div className={`grid grid-cols-1 ${stageFilter ? 'md:grid-cols-1' : 'md:grid-cols-6'} gap-3 pb-8`}>
              {STAGES.filter(s => !stageFilter || s === stageFilter).map((s) => (
                <div key={s} className="min-w-0" data-testid={`kanban-col-${s}`}>
                  <div className="mb-2 px-2 flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${STAGE_META[s].color.split(' ')[1]}`}>{STAGE_META[s].label}</span>
                    <span className="text-xs text-slate-400">{stageCounts[s] || 0}</span>
                  </div>
                  <div className={`space-y-2 min-h-[100px] ${stageFilter ? 'grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2' : ''}`}>
                    {apps.filter(a => a.stage === s).map((a) => (
                      <Card key={a.id} className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => setDetailApp(a)} data-testid={`app-card-${a.id}`}>
                        <CardContent className="p-3">
                          <p className="font-semibold text-slate-800 text-sm truncate">{a.full_name}</p>
                          <p className="text-xs text-slate-500 truncate">{a.job_title}</p>
                          <p className="text-[11px] text-slate-400 mt-1">{(a.created_at || '').split('T')[0]}</p>
                        </CardContent>
                      </Card>
                    ))}
                    {apps.filter(a => a.stage === s).length === 0 && (
                      <p className="text-xs text-slate-400 italic px-2 py-4 text-center">No applications</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="jobs">
            <div className="flex justify-end mb-3">
              <Button size="sm" onClick={openCreateJob} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-job-btn">
                <Plus className="w-4 h-4 mr-1" /> New job posting
              </Button>
            </div>
            <div className="space-y-3">
              {jobs.map((j) => (
                <Card key={j.id} data-testid={`job-row-${j.slug}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{j.department}</Badge>
                        <Badge className={j.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                          {j.status === 'open' ? 'Published' : 'Unpublished'}
                        </Badge>
                        <Badge variant="outline" className="text-xs">{j.application_count || 0} applications</Badge>
                        <Badge variant="outline" className="text-xs">/{j.slug}</Badge>
                      </div>
                      <h3 className="font-semibold text-slate-800">{j.title}</h3>
                      <p className="text-xs text-slate-500 truncate">{j.summary}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                      <Link to={`/careers/${j.slug || j.id}`} target="_blank">
                        <Button variant="outline" size="sm" data-testid={`preview-job-${j.slug}`}>Preview</Button>
                      </Link>
                      <Button variant="outline" size="sm" onClick={() => openEditJob(j)} data-testid={`edit-job-${j.slug}`}>
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      {j.status === 'open' ? (
                        <Button variant="outline" size="sm" onClick={() => closeJob(j)} data-testid={`unpublish-job-${j.slug}`}>
                          <EyeOff className="w-3 h-3 mr-1" /> Unpublish
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => reopenJob(j)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`publish-job-${j.slug}`}>
                          <Eye className="w-3 h-3 mr-1" /> Publish
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => deleteJob(j)} className="text-red-600 hover:bg-red-50" data-testid={`delete-job-${j.slug}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Job posting editor dialog */}
      <Dialog open={!!jobEditor} onOpenChange={(o) => !o && setJobEditor(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="job-editor-dialog">
          <DialogHeader>
            <DialogTitle>{jobEditor?.mode === 'create' ? 'New job posting' : 'Edit job posting'}</DialogTitle>
            <DialogDescription>
              {jobEditor?.mode === 'create'
                ? 'Fill in the details — the posting will go live on /careers immediately.'
                : 'Changes save immediately. Toggle status to unpublish.'}
            </DialogDescription>
          </DialogHeader>
          {jobEditor && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Job title</label>
                  <Input value={jobEditor.job.title} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, title: e.target.value } })} placeholder="Social Media Intern" data-testid="job-title-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">URL slug</label>
                  <Input value={jobEditor.job.slug} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, slug: e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase() } })} placeholder="social-media-intern" data-testid="job-slug-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Department</label>
                  <Input value={jobEditor.job.department} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, department: e.target.value } })} placeholder="Marketing" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Role key (permissions template)</label>
                  <Input value={jobEditor.job.role_key} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, role_key: e.target.value } })} placeholder="social_media" data-testid="job-role-key-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Location</label>
                  <Input value={jobEditor.job.location} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, location: e.target.value } })} placeholder="Remote" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Status</label>
                  <Select value={jobEditor.job.status} onValueChange={(v) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, status: v } })}>
                    <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Published (accepting applications)</SelectItem>
                      <SelectItem value="closed">Unpublished (hidden from /careers)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Hours per week</label>
                  <Input type="number" value={jobEditor.job.hours_per_week} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, hours_per_week: Number(e.target.value) || 0 } })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Duration (weeks)</label>
                  <Input type="number" value={jobEditor.job.duration_weeks} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, duration_weeks: Number(e.target.value) || 0 } })} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Short summary (shown on the careers page card)</label>
                <Textarea rows={2} value={jobEditor.job.summary} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, summary: e.target.value } })} data-testid="job-summary-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Full description (Markdown supported)</label>
                <Textarea rows={5} value={jobEditor.job.description || ''} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, description: e.target.value } })} data-testid="job-description-input" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Responsibilities (one per line)</label>
                  <Textarea rows={4} value={(jobEditor.job.responsibilities || []).join('\n')} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, responsibilities: e.target.value.split('\n').filter(Boolean) } })} placeholder="Own weekly TikTok cadence&#10;Draft campaign copy&#10;..." />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Perks (one per line)</label>
                  <Textarea rows={4} value={(jobEditor.job.perks || []).join('\n')} onChange={(e) => setJobEditor({ ...jobEditor, job: { ...jobEditor.job, perks: e.target.value.split('\n').filter(Boolean) } })} placeholder="Portfolio piece&#10;Recommendation letter&#10;..." />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setJobEditor(null)}>Cancel</Button>
            <Button onClick={saveJobEditor} disabled={savingJob} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-job-btn">
              {savingJob ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {jobEditor?.mode === 'create' ? 'Publish job' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application detail dialog */}
      <Dialog open={!!detailApp} onOpenChange={(o) => !o && setDetailApp(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-detail-dialog">
          {detailApp && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{detailApp.full_name}</DialogTitle>
                <DialogDescription>{detailApp.job_title} · Applied {(detailApp.created_at || '').split('T')[0]}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className={STAGE_META[detailApp.stage].color}>{STAGE_META[detailApp.stage].label}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <InfoRow icon={Mail} label="Email" value={detailApp.email} />
                  <InfoRow icon={Phone} label="Phone" value={detailApp.phone || '—'} />
                  <InfoRow icon={GraduationCap} label="School" value={`${detailApp.school || '—'} ${detailApp.graduation_year || ''}`} />
                  <InfoRow icon={Clock} label="Hours/week" value={detailApp.availability_hours_per_week || '—'} />
                  <InfoRow icon={Link2} label="LinkedIn" value={detailApp.linkedin_url || '—'} href={detailApp.linkedin_url} />
                  <InfoRow icon={Link2} label="Portfolio" value={detailApp.portfolio_url || '—'} href={detailApp.portfolio_url} />
                </div>

                <div>
                  <h4 className="text-xs uppercase font-semibold text-slate-500 mb-1">Cover Letter</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{detailApp.cover_letter}</p>
                </div>

                <div>
                  <h4 className="text-xs uppercase font-semibold text-slate-500 mb-1">Referral</h4>
                  <p className="text-sm text-slate-700">{detailApp.referral_source || '—'}</p>
                </div>

                <div>
                  <h4 className="text-xs uppercase font-semibold text-slate-500 mb-2">Timeline</h4>
                  <div className="space-y-1.5">
                    {(detailApp.timeline || []).map((t, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <Badge className={STAGE_META[t.stage]?.color || 'bg-slate-100'}>{STAGE_META[t.stage]?.label || t.stage}</Badge>
                        <div className="flex-1">
                          <span className="text-slate-500">{(t.at || '').replace('T', ' ').split('.')[0]}</span>
                          {t.note && <p className="text-slate-700 mt-0.5">{t.note}</p>}
                          {t.by && <span className="text-slate-400"> · {t.by}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pipeline extras — Interviews, Messages, Resources */}
              <div className="border-t pt-3 mt-3">
                <div className="flex gap-1 mb-2 border-b">
                  {[
                    { k: 'overview',   label: 'Overview' },
                    { k: 'interviews', label: `Interviews (${(detailApp.interviews || []).length})` },
                    { k: 'messages',   label: `Messages (${(detailApp.candidate_messages || []).length})` },
                    { k: 'resources',  label: `Resources (${(detailApp.resources || []).length})` },
                  ].map((t) => (
                    <button
                      key={t.k}
                      onClick={() => setPipelineTab(t.k)}
                      className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px ${pipelineTab === t.k ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                      data-testid={`app-tab-${t.k}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {pipelineTab === 'interviews' && (
                  <div className="space-y-2" data-testid="pipeline-interviews">
                    {(detailApp.interviews || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic">No interviews scheduled yet.</p>
                    )}
                    {(detailApp.interviews || []).map((iv) => (
                      <div key={iv.id} className="p-2 rounded-md bg-slate-50 border text-xs">
                        <p className="font-semibold text-slate-700">{new Date(iv.scheduled_at).toLocaleString()} · {iv.mode} · {iv.duration_min}m</p>
                        {iv.meeting_link && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-sky-600 underline break-all">{iv.meeting_link}</a>}
                        {iv.interviewer_name && <p className="text-slate-500">Interviewer: {iv.interviewer_name}</p>}
                        {iv.notes && <p className="text-slate-600 mt-1 whitespace-pre-wrap">{iv.notes}</p>}
                        <div className="flex gap-1 mt-1">
                          <Badge className="bg-slate-100 text-slate-700">{iv.status || 'scheduled'}</Badge>
                          {iv.status !== 'completed' && <Button size="sm" variant="ghost" onClick={() => updateInterviewStatus(iv.id, 'completed')} className="h-6 text-[10px]">Mark completed</Button>}
                          {iv.status !== 'cancelled' && <Button size="sm" variant="ghost" onClick={() => updateInterviewStatus(iv.id, 'cancelled')} className="h-6 text-[10px] text-red-600">Cancel</Button>}
                        </div>
                      </div>
                    ))}
                    <div className="p-2 border rounded-md bg-white space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Schedule new</p>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="datetime-local" value={interviewDraft.scheduled_at} onChange={(e) => setInterviewDraft({ ...interviewDraft, scheduled_at: e.target.value })} className="border rounded px-2 py-1 text-xs" data-testid="interview-datetime" />
                        <input type="number" value={interviewDraft.duration_min} onChange={(e) => setInterviewDraft({ ...interviewDraft, duration_min: parseInt(e.target.value, 10) || 30 })} className="border rounded px-2 py-1 text-xs" placeholder="Duration (min)" />
                        <select value={interviewDraft.mode} onChange={(e) => setInterviewDraft({ ...interviewDraft, mode: e.target.value })} className="border rounded px-2 py-1 text-xs">
                          <option value="video">Video</option><option value="phone">Phone</option><option value="in_person">In person</option>
                        </select>
                        <input type="url" placeholder="Meeting link (optional)" value={interviewDraft.meeting_link} onChange={(e) => setInterviewDraft({ ...interviewDraft, meeting_link: e.target.value })} className="border rounded px-2 py-1 text-xs" />
                      </div>
                      <Textarea placeholder="Notes for the candidate / interviewer" rows={2} value={interviewDraft.notes} onChange={(e) => setInterviewDraft({ ...interviewDraft, notes: e.target.value })} className="text-xs" />
                      <Button size="sm" onClick={addInterview} disabled={pipelineBusy} className="bg-emerald-500 hover:bg-emerald-600 h-7 text-xs" data-testid="interview-add-btn">
                        {pipelineBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}Schedule interview
                      </Button>
                    </div>
                  </div>
                )}

                {pipelineTab === 'messages' && (
                  <div className="space-y-2" data-testid="pipeline-messages">
                    {(detailApp.candidate_messages || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic">No candidate messages yet.</p>
                    )}
                    {(detailApp.candidate_messages || []).map((m) => (
                      <div key={m.id} className="p-2 rounded-md bg-sky-50 border border-sky-100 text-xs">
                        <p className="font-semibold text-slate-700">{m.created_by_name || m.created_by} · {(m.created_at || '').replace('T', ' ').slice(0, 16)}</p>
                        {m.emailed && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] mr-1">emailed</Badge>}
                        <p className="text-slate-700 mt-1 whitespace-pre-wrap">{m.message}</p>
                      </div>
                    ))}
                    <div className="p-2 border rounded-md bg-white space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Send new update</p>
                      <Textarea placeholder="Write an update to the candidate — will show in timeline (and optionally email)." rows={3} value={messageDraft.message} onChange={(e) => setMessageDraft({ ...messageDraft, message: e.target.value })} className="text-xs" data-testid="message-input" />
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={messageDraft.send_email} onChange={(e) => setMessageDraft({ ...messageDraft, send_email: e.target.checked })} /> Email the candidate
                      </label>
                      <Button size="sm" onClick={addMessage} disabled={pipelineBusy || !messageDraft.message.trim()} className="bg-sky-500 hover:bg-sky-600 h-7 text-xs" data-testid="message-send-btn">
                        {pipelineBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}Send
                      </Button>
                    </div>
                  </div>
                )}

                {pipelineTab === 'resources' && (
                  <div className="space-y-2" data-testid="pipeline-resources">
                    {(detailApp.resources || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic">No resources attached yet.</p>
                    )}
                    {(detailApp.resources || []).map((r) => (
                      <div key={r.id} className="p-2 rounded-md bg-amber-50 border border-amber-100 text-xs">
                        <p className="font-semibold text-slate-700">{r.title}</p>
                        {r.kind === 'url' && r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-sky-600 underline break-all">{r.url}</a>}
                        {r.note && <p className="text-slate-700 mt-1 whitespace-pre-wrap">{r.note}</p>}
                        <p className="text-[10px] text-slate-400 mt-1">added by {r.added_by} · {(r.added_at || '').split('T')[0]}</p>
                      </div>
                    ))}
                    <div className="p-2 border rounded-md bg-white space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Attach resource</p>
                      <div className="grid grid-cols-2 gap-2">
                        <select value={resourceDraft.kind} onChange={(e) => setResourceDraft({ ...resourceDraft, kind: e.target.value })} className="border rounded px-2 py-1 text-xs">
                          <option value="url">Link (URL)</option><option value="note">Internal note</option>
                        </select>
                        <input type="text" placeholder="Title" value={resourceDraft.title} onChange={(e) => setResourceDraft({ ...resourceDraft, title: e.target.value })} className="border rounded px-2 py-1 text-xs" data-testid="resource-title" />
                      </div>
                      {resourceDraft.kind === 'url' ? (
                        <input type="url" placeholder="https://…" value={resourceDraft.url} onChange={(e) => setResourceDraft({ ...resourceDraft, url: e.target.value })} className="border rounded px-2 py-1 text-xs w-full" />
                      ) : (
                        <Textarea placeholder="Internal note (visible to hiring team)" rows={2} value={resourceDraft.note} onChange={(e) => setResourceDraft({ ...resourceDraft, note: e.target.value })} className="text-xs" />
                      )}
                      <Button size="sm" onClick={addResource} disabled={pipelineBusy || !resourceDraft.title.trim()} className="bg-amber-500 hover:bg-amber-600 h-7 text-xs" data-testid="resource-add-btn">
                        {pipelineBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}Attach
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <div className="flex flex-wrap gap-2 w-full">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateOfferLetter(detailApp)}
                    disabled={generatingLetter}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="generate-offer-letter-btn"
                  >
                    {generatingLetter ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                    Generate offer letter
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendOfferEmail(detailApp)}
                    disabled={sendingOfferId === detailApp.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="send-offer-email-btn"
                  >
                    {sendingOfferId === detailApp.id
                      ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      : <Mail className="w-3 h-3 mr-1" />}
                    {detailApp.offer_sent_at ? 'Re-send offer email' : 'Email offer (Accept / Decline)'}
                  </Button>
                  {detailApp.offer_sent_at && (
                    <div className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1 rounded bg-slate-50 border border-slate-200" data-testid="offer-status-chip">
                      <span className="font-semibold">Offer sent:</span>
                      <span>{new Date(detailApp.offer_sent_at).toLocaleString()}</span>
                      {detailApp.offer_response && (
                        <span className={detailApp.offer_response === 'accept' ? 'text-emerald-700 font-bold' : 'text-red-700 font-bold'}>
                          · Applicant {detailApp.offer_response === 'accept' ? 'ACCEPTED' : 'DECLINED'}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex-1" />
                  {STAGES.filter(s => s !== detailApp.stage).map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={s === 'hired' ? 'default' : 'outline'}
                      className={s === 'hired' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                      onClick={() => { setDetailApp(null); openStageChange(detailApp, s); }}
                      data-testid={`move-to-${s}-btn`}
                    >
                      {s === 'hired' && <UserPlus className="w-3 h-3 mr-1" />}
                      Move to {STAGE_META[s].label}
                    </Button>
                  ))}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Stage note dialog */}
      <Dialog open={!!stageDialog} onOpenChange={(o) => !o && setStageDialog(null)}>
        <DialogContent className="max-w-md" data-testid="stage-change-dialog">
          {stageDialog && (
            <>
              <DialogHeader>
                <DialogTitle>Move to {STAGE_META[stageDialog.targetStage]?.label}</DialogTitle>
                <DialogDescription>{stageDialog.app.full_name} · {stageDialog.app.job_title}</DialogDescription>
              </DialogHeader>
              <Textarea placeholder="Note (optional — visible in timeline)" value={stageNote} onChange={(e) => setStageNote(e.target.value)} rows={3} data-testid="stage-note-input" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setStageDialog(null)}><X className="w-4 h-4 mr-1" />Cancel</Button>
                <Button onClick={applyStageChange} data-testid="stage-confirm-btn"><Save className="w-4 h-4 mr-1" />Confirm</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Hire dialog */}
      <Dialog open={!!hireDialog} onOpenChange={(o) => !o && setHireDialog(null)}>
        <DialogContent className="max-w-md" data-testid="hire-dialog">
          {hireDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-500" /> Hire {hireDialog.full_name}</DialogTitle>
                <DialogDescription>
                  Provisions a staff account with the <strong>{hireDialog.role_key}</strong> role and role-based permissions.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    id="create-account"
                    checked={hireForm.create_account}
                    onChange={(e) => setHireForm(f => ({ ...f, create_account: e.target.checked }))}
                    data-testid="hire-create-account"
                  />
                  <label htmlFor="create-account">Create staff account & grant role permissions</label>
                </div>
                {hireForm.create_account && (
                  <div>
                    <label className="text-xs">Temporary password (optional)</label>
                    <Input
                      placeholder="Auto-generated if blank"
                      value={hireForm.temporary_password}
                      onChange={(e) => setHireForm(f => ({ ...f, temporary_password: e.target.value }))}
                      data-testid="hire-password"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs">Start date</label>
                  <Input type="date" value={hireForm.start_date} onChange={(e) => setHireForm(f => ({ ...f, start_date: e.target.value }))} data-testid="hire-start-date" />
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => generateOfferLetter(hireDialog)}
                  disabled={generatingLetter}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="hire-generate-letter-btn"
                >
                  {generatingLetter ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                  Preview offer letter
                </Button>
                <div className="flex-1" />
                <Button variant="outline" onClick={() => setHireDialog(null)}><X className="w-4 h-4 mr-1" />Cancel</Button>
                <Button onClick={submitHire} className="bg-emerald-500 hover:bg-emerald-600" data-testid="hire-confirm-btn">
                  <UserPlus className="w-4 h-4 mr-1" /> Hire & onboard
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, href }) => (
  <div className="flex items-start gap-2 min-w-0">
    <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-slate-500">{label}</p>
      {href && href !== '—' ? (
        <a href={href} target="_blank" rel="noreferrer" className="text-sm text-emerald-600 hover:underline truncate block">{value}</a>
      ) : (
        <p className="text-sm text-slate-800 truncate">{value}</p>
      )}
    </div>
  </div>
);

export default AdminCareersDashboard;
